import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { forkJoin, from, map, Observable, of } from 'rxjs';
import { DateTime } from 'luxon';
import type DataSource from 'devextreme/data/data_source';
import { DxOdataStoreService } from '@shared/components/dx-data-grid/data-access';
import { getDateTime, normalizeId, TODataGuid } from '@shared/utils';
import { DateTimeService } from '@application/data-access';
import { ReconciliationStatus } from '@domains/reconciliation/util';
import {
  CashFlowAggregates,
  CashFlowAggregatesScope,
  CashFlowEntriesByDayMap,
  CashFlowEntry,
  CashFlowReportLine,
  CashOperation,
  CashSession,
  CashTransfer,
  FinancialAccount,
  FinancialAccountType,
  TFinancialAccount,
  TenantDailyAggregatePoint,
  TenantDailySeriesPoint,
  TreasuryDateRange,
  TreasuryHomeRequest,
  ITreasurySettings,
  TreasuryOpenSessionRequest,
  TreasuryCloseSessionRequest,
  TreasuryFinalizeSessionRequest,
  TreasuryApproveSessionRequest,
  TreasuryRejectSessionRequest,
  TreasuryReopenSessionRequest,
  TreasuryManualCashFlowEntryRequest,
  TreasuryCreateCashOperationRequest,
  TreasuryCreateCashTransferRequest,
} from '../entities/treasury.model';
import { CashSessionStatus } from '@domains/treasury/util';
import type { TreasuryDashboardPayload } from '@domains/treasury/util';
import { APP_CONFIG } from '@application/config';
import {
  mapCashFlowDirectionAggregateRows,
  mapCashFlowEntriesByDayRows,
  mapCashFlowSumAggregateRow,
  ODataCashFlowDayAggregateResponse,
  ODataCashFlowDayAggregateRow,
  ODataCashFlowDayKeyResponse,
  ODataCashFlowDayKeyRow,
  mapODataDailySeriesResponse,
  mapODataRowToCashFlowEntry,
  mapODataRowToCashSession,
  mapODataTenantDailyAggregateResponse,
  ODataCashFlowDirectionAggregateResponse,
  ODataCashFlowDirectionAggregateRow,
  ODataCashFlowEntryRow,
  ODataCashFlowSumAggregateResponse,
  ODataCashFlowSumAggregateRow,
  ODataDailySeriesRow,
  ODataTenantDailyAggregateRow,
} from './cashflow-entry-odata.mapper';

/** Pre-scope applied to `cashFlowEntriesDataSource` as a single OData `$filter`. */
export interface TreasuryCashFlowEntriesScope {
  financialAccountId?: string;
  cashSessionId?: string;
  /** Adds `ReconciledTransactionId eq null` to the filter. */
  unreconciledOnly?: boolean;
  /** Adds `contains(tolower(Description|Category), <text>)` to the filter. */
  searchText?: string;
  /**
   * Adds `ChartOfAccountsNodeId eq <guid>` — DRE Gerencial drill-down
   * starting from a chart-of-accounts leaf row.
   */
  chartOfAccountsNodeId?: string;
  /**
   * Closed range over `CompetenceDate`. Accepts ISO datetime or DateOnly
   * (`yyyy-MM-dd`); the conversion to DateOnly + boundary `T00:00:00Z` /
   * `T23:59:59Z` happens here at the HTTP boundary.
   */
  competenceDateRange?: {
    readonly startDate: string;
    readonly endDate: string;
  };
  /**
   * When `true`, applies the same eligibility criteria as the
   * `DreReportController`: `Status eq 'Confirmed'` and exclusion of the
   * types that do not compose the DRE (`Transfer`/`Supply`/`Withdrawal`).
   * Used by the DRE Gerencial drill-down to ensure the panel's sum matches
   * the leaf's `Amount`.
   */
  dreEligibleOnly?: boolean;
  /**
   * Verbatim OData `$filter` expression. Highest priority — when present it
   * replaces the per-field clauses above (the intended source is the grid's
   * outgoing `$filter`, captured via `exposeRequest`, so aggregations stay in
   * lockstep with the grid's header filters / search).
   */
  rawFilter?: string | null;
}

const CASH_FLOW_ENTRIES_FIELD_TYPES = {
  CashFlowEntryId: 'Guid',
  FinancialAccountId: 'Guid',
  CashSessionId: 'Guid',
  OriginId: 'Guid',
  CashFlowEntryRequestId: 'Guid',
  PersonId: 'Guid',
  AddedBy: 'Guid',
  MatchedBankTransactionId: 'Guid',
  ReconciledTransactionId: 'Guid',
} as const;

/**
 * Hard cap for the one-shot session-entries fetch. The design assumes a
 * cash session = one operator working day and fits in a single page. If the
 * server reports `totalCount > SESSION_ENTRIES_PAGE_CAP` the load was silently
 * truncated and the turn totals/list are wrong — `getSessionCashFlowEntries`
 * warns loudly so the assumption breaking shows up in ops logs / Sentry.
 */
const SESSION_ENTRIES_PAGE_CAP = 1000;

/**
 * OData `$select` for `getSessionCashFlowEntries`. The full row carries
 * reconciliation / origin / split-amount columns the turn detail never reads;
 * trimming the projection cuts the payload meaningfully across a full day.
 *
 * Readers (keep in sync — a missing field surfaces as null/0 in the UI):
 * - `treasury.store.ts` → `mergePersonNames` (PersonId, AddedBy)
 * - `treasury-cash-turn-feature.component.ts` totals (CashSessionId, Direction, Amount, EntryType)
 * - `treasury-cash-movements-timeline-ui.component.ts` row VM
 * - `treasury-cash-movement-detail-aside-ui.component.ts` detail panel
 * - `treasury-cash-turns-print.service.ts` print path
 */
const SESSION_ENTRIES_SELECT = [
  'CashFlowEntryId',
  'FinancialAccountId',
  'CashSessionId',
  'Direction',
  'EntryType',
  'Amount',
  'Status',
  'Description',
  'CompetenceDate',
  'PaymentDate',
  'Category',
  'PaymentMethod',
  'AddedOnUtc',
  'Details',
  'CardAmount',
  'CardBrand',
  'CardNumber',
  'CardholderName',
  'TId',
  'RequestDescription',
  'PersonId',
  'AddedBy',
].join(',');

@Injectable({ providedIn: 'root' })
export class TreasuryService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(APP_CONFIG).apiUrl.treasury_service;
  private readonly dxOdataStoreService = inject(DxOdataStoreService);
  private readonly dateTimeService = inject(DateTimeService);

  public getTreasurySettings(): Observable<ITreasurySettings> {
    return this.http.get<ITreasurySettings>(`${this.baseUrl}/TreasurySettings`, {
      withCredentials: true,
    });
  }

  public saveTreasurySettings(
    settings: ITreasurySettings,
  ): Observable<ITreasurySettings> {
    return this.http.put<ITreasurySettings>(
      `${this.baseUrl}/TreasurySettings`,
      settings,
      { withCredentials: true },
    );
  }

  public getFinancialAccounts(): Observable<FinancialAccount[]> {
    return this.http
      .request<FinancialAccount[]>('OPTIONS', `${this.baseUrl}/FinancialAccount`, {
        withCredentials: true,
      })
      .pipe(map((body) => (Array.isArray(body) ? body : [])));
  }

  public getPhysicalFinancialAccounts(): Observable<TFinancialAccount[]> {
    return this.getFinancialAccounts().pipe(
      map((accounts) =>
        accounts.filter((account) => this.isPhysicalAccount(account)),
      ),
    );
  }

  public getBankFinancialAccounts(): Observable<TFinancialAccount[]> {
    return this.getFinancialAccounts().pipe(
      map((accounts) =>
        accounts.filter((account) => account.Type === FinancialAccountType.Bank),
      ),
    );
  }

  private isPhysicalAccount(account: TFinancialAccount): boolean {
    return account.Type === FinancialAccountType.Cash;
  }

  public getFinancialAccount(id: string): Observable<FinancialAccount | null> {
    return this.http
      .get<FinancialAccount | FinancialAccount[] | null>(
        `${this.baseUrl}/FinancialAccount`,
        {
          params: new HttpParams({ fromObject: { id } }),
          withCredentials: true,
        },
      )
      .pipe(map((body) => this.singleOrNull(body)));
  }

  public createFinancialAccount(
    payload: Partial<FinancialAccount>,
  ): Observable<FinancialAccount | null> {
    return this.http
      .post<FinancialAccount | FinancialAccount[] | null>(
        `${this.baseUrl}/FinancialAccount`,
        payload,
        {
          withCredentials: true,
        },
      )
      .pipe(map((body) => this.singleOrNull(body)));
  }

  public updateFinancialAccount(
    payload: Partial<FinancialAccount>,
  ): Observable<FinancialAccount | null> {
    return this.http
      .patch<FinancialAccount | FinancialAccount[] | null>(
        `${this.baseUrl}/FinancialAccount`,
        payload,
        {
          withCredentials: true,
        },
      )
      .pipe(map((body) => this.singleOrNull(body)));
  }

  public deleteFinancialAccount(id: string): Observable<unknown> {
    return this.http.delete<unknown>(`${this.baseUrl}/FinancialAccount`, {
      params: new HttpParams({ fromObject: { id } }),
      withCredentials: true,
    });
  }

  /** GET `/CashSession?id=` — `id` is **CashSessionId** (turn), not a cash-flow line id. */
  public getCashSession(id: string): Observable<CashSession | null> {
    return this.http
      .get<CashSession | CashSession[] | null>(`${this.baseUrl}/CashSession`, {
        params: new HttpParams({ fromObject: { id } }),
        withCredentials: true,
      })
      .pipe(map((body) => this.singleOrNull(body)));
  }

  /**
   * GET `/CashSession/GetMyOpenSession` — returns the current user's open session(s) (for receipts).
   * Response may be a single session, an array, or null/empty.
   */
  public getMyOpenSessions(): Observable<CashSession[]> {
    return this.http
      .get<CashSession | CashSession[] | null>(
        `${this.baseUrl}/CashSession/GetMyOpenSession`,
        {
          withCredentials: true,
        },
      )
      .pipe(
        map((body) => {
          if (body == null) return [];
          if (Array.isArray(body)) return body;
          return [body];
        }),
      );
  }

  /**
   * Targeted OData fetchers for the home & per-account pages, hitting the
   * `/OData/CashSessions` endpoint. They replaced the legacy
   * `OPTIONS /CashSession` no-filter dump which forced every caller to pull
   * the full session history and bucket it client-side.
   *
   * OData v4 literal rules on this backend (relevant for the filter strings):
   * - Edm.Guid literals are bare UUIDs, no `guid'...'` wrapper / no quotes.
   *   Quoting promotes them to Edm.String and the backend rejects with a
   *   binary-operator-type-mismatch ODataException.
   * - Edm.DateTimeOffset literals are bare ISO-8601 strings, no wrapper.
   * - `Status` is the numeric `CashSessionStatus` enum (0–3) — used unquoted.
   */
  public getOpenCashSessions(): Observable<CashSession[]> {
    return this.getCashSessionList(`Status eq ${CashSessionStatus.Open}`);
  }

  public getPendingCashSessions(): Observable<CashSession[]> {
    return this.getCashSessionList(
      `Status eq ${CashSessionStatus.Closing} or Status eq ${CashSessionStatus.PendingReview}`,
    );
  }

  /**
   * Operator's own sessions opened within the recent window (any status).
   * Replaces the legacy `getMyRecentSessions` which used `OPTIONS /CashSession`
   * — that filter was rejected by the backend, so the store fell back to
   * pulling everything and filtering client-side.
   */
  public getMyRecentCashSessions(params: {
    operatorPersonId: string;
    fromUtc: string;
  }): Observable<CashSession[]> {
    return this.getCashSessionList(
      `OpenedBy eq ${params.operatorPersonId} and OpenedOnUtc ge ${params.fromUtc}`,
    );
  }

  /**
   * Server-side `DataSource<CashSession>` for the per-account "Histórico de
   * caixa" sidebar. Filter / ordering pushed down to OData so the consumer
   * can drive pagination via `ds.pageIndex(N).load()` and feed an
   * `InfiniteScroll` directive without re-sorting client-side (which would
   * silently no-op because `formatUtcDatesInData` turns `OpenedOnUtc`
   * strings into Date objects that `getDateTime` parses as invalid).
   *
   * Each call returns a fresh DataSource — memoize at the component level
   * when binding to a long-lived view.
   */
  public accountCashSessionsDataSource(
    financialAccountId: string,
  ): DataSource<CashSession> {
    const ds = this.dxOdataStoreService.fromOdata<CashSession>({
      url: this.baseUrl,
      metadata: 'OData/CashSessions',
      key: 'CashSessionId',
      params: { $filter: `FinancialAccountId eq ${financialAccountId}` },
      fieldTypes: {
        CashSessionId: 'Guid',
        FinancialAccountId: 'Guid',
        OpenedBy: 'Guid',
        ClosedBy: 'Guid',
      },
    });
    ds.sort({ selector: 'OpenedOnUtc', desc: true });
    ds.requireTotalCount(true);
    return ds;
  }

  /**
   * Batched `count(CashFlowEntries)` per `CashSessionId`, replacing the N
   * sequential `countCashFlowEntriesForSession` HTTP calls the home used to
   * fire (one per open session). Resolves to a plain map keyed by session id.
   *
   * Returns `{}` when `cashSessionIds` is empty — saves a pointless request.
   */
  public getCashFlowEntryCountsForSessions(
    cashSessionIds: readonly string[],
  ): Observable<Record<string, number>> {
    if (cashSessionIds.length === 0) {
      return of({} as Record<string, number>);
    }
    const filter = cashSessionIds
      .map((id) => `CashSessionId eq ${id}`)
      .join(' or ');
    const apply =
      `filter(${filter})/` +
      `groupby((CashSessionId),aggregate($count as Count))`;
    const ds = this.dxOdataStoreService.fromOdata<{
      CashSessionId: TODataGuid;
      Count: number;
    }>({
      url: this.baseUrl,
      metadata: 'OData/CashFlowEntries',
      key: 'CashFlowEntryId',
      params: { $apply: apply },
      fieldTypes: { ...CASH_FLOW_ENTRIES_FIELD_TYPES },
    });
    return from(
      ds.load().then((rows) => {
        const map: Record<string, number> = {};
        for (const id of cashSessionIds) {
          map[id] = 0;
        }
        const list = (rows ?? []) as { CashSessionId: TODataGuid; Count: number }[];
        for (const row of list) {
          // `fieldTypes` declares `CashSessionId` as `Guid`, so DX wraps
          // the value in an `{ _value }` envelope on the way in;
          // `normalizeId` unwraps it (and tolerates plain strings) so the
          // map key is always a primitive uuid.
          const id = normalizeId(row.CashSessionId);
          if (!id) continue;
          map[id] = row.Count ?? 0;
        }
        return map;
      }),
    );
  }

  private getCashSessionList(
    filter: string,
    options?: {
      sort?: { selector: keyof CashSession; desc?: boolean };
    },
  ): Observable<CashSession[]> {
    // Goes through `DxOdataStoreService.fromOdata` so the request inherits
    // the same conventions every other CashSession path uses: bearer token
    // injection, Edm.Guid serialization driven by `fieldTypes`, and the
    // wall-clock-as-UTC date adjustment that `adjustODataFilterDatesToUtc`
    // applies to `$filter` clauses on `*Utc` fields (relevant for
    // `getMyRecentCashSessions`, whose filter mentions `OpenedOnUtc`).
    const ds = this.dxOdataStoreService.fromOdata<CashSession>({
      url: this.baseUrl,
      metadata: 'OData/CashSessions',
      key: 'CashSessionId',
      params: { $filter: filter },
      fieldTypes: {
        CashSessionId: 'Guid',
        FinancialAccountId: 'Guid',
        OpenedBy: 'Guid',
        ClosedBy: 'Guid',
      },
    });
    if (options?.sort) {
      // Pushed down to OData as `$orderby=<selector> desc?` — keeps the
      // ordering server-side so client doesn't need to fight `formatUtcDatesInData`
      // turning `*Utc` strings into Date instances.
      ds.sort(options.sort);
    }
    // Every caller of this helper (`getOpenCashSessions`,
    // `getPendingCashSessions`, `getMyRecentCashSessions`) wants the FULL
    // result set — no `$top` should land on the wire. DX `DataSource`
    // defaults to paginated mode with `pageSize=20`, which would silently
    // cap each list at 20 rows. Turn pagination off so the request brings
    // every matching session in one round-trip. The paginated path is
    // handled separately by `accountCashSessionsDataSource` (sidebar with
    // InfiniteScroll), which builds its own DataSource and keeps paging on.
    ds.paginate(false);
    // DX wraps every `Guid` field in a `{ _value }` envelope on the way back.
    // Normalize at the boundary so the slots (and every downstream Map /
    // equality check / router param) see plain UUID strings.
    return from(
      ds.load().then((rows) =>
        ((rows ?? []) as CashSession[]).map(mapODataRowToCashSession),
      ),
    );
  }

  public openCashSession(
    request: TreasuryOpenSessionRequest,
  ): Observable<CashSession | null> {
    return this.http
      .post<CashSession | CashSession[] | null>(
        `${this.baseUrl}/CashSession/OpenSession`,
        null,
        {
          params: this.toQueryParams(request),
          withCredentials: true,
        },
      )
      .pipe(map((body) => this.singleOrNull(body)));
  }

  public closeCashSession(
    request: TreasuryCloseSessionRequest,
  ): Observable<CashSession | null> {
    return this.http
      .post<CashSession | CashSession[] | null>(
        `${this.baseUrl}/CashSession/CloseSession`,
        null,
        {
          params: this.toQueryParams(request),
          withCredentials: true,
        },
      )
      .pipe(map((body) => this.singleOrNull(body)));
  }

  public finalizeCashSession(
    request: TreasuryFinalizeSessionRequest,
  ): Observable<CashSession | null> {
    return this.http
      .post<CashSession | CashSession[] | null>(
        `${this.baseUrl}/CashSession/FinalizeSession`,
        null,
        {
          params: this.toQueryParams(request),
          withCredentials: true,
        },
      )
      .pipe(map((body) => this.singleOrNull(body)));
  }

  public reopenCashSession(
    request: TreasuryReopenSessionRequest,
  ): Observable<CashSession | null> {
    return this.http
      .post<CashSession | CashSession[] | null>(
        `${this.baseUrl}/CashSession/ReopenSession`,
        null,
        {
          params: this.toQueryParams(request),
          withCredentials: true,
        },
      )
      .pipe(map((body) => this.singleOrNull(body)));
  }

  public approveCashSession(
    request: TreasuryApproveSessionRequest,
  ): Observable<CashSession | null> {
    return this.http
      .post<CashSession | CashSession[] | null>(
        `${this.baseUrl}/CashSession/ApproveSession`,
        null,
        {
          params: this.toQueryParams(request),
          withCredentials: true,
        },
      )
      .pipe(map((body) => this.singleOrNull(body)));
  }

  public rejectCashSession(
    request: TreasuryRejectSessionRequest,
  ): Observable<CashSession | null> {
    return this.http
      .post<CashSession | CashSession[] | null>(
        `${this.baseUrl}/CashSession/RejectSession`,
        null,
        {
          params: this.toQueryParams(request),
          withCredentials: true,
        },
      )
      .pipe(map((body) => this.singleOrNull(body)));
  }

  /**
   * Server-side `DataSource` backed by `GET /OData/CashFlowEntries`.
   * Filter/sort/paging/search are pushed down to OData via the grid's
   * `remoteOperations`. Optional `scope` pre-applies a `$filter` so callers
   * (per-account page, per-session detail, manual-match candidates) don't
   * repeat scoping in the grid.
   *
   * `searchText` is encoded as a case-insensitive contains() against
   * Description / Category via OData `tolower(...)` — the caller should pass
   * the already-trimmed text (any case).
   *
   * Each call returns a fresh DataSource — memoize at the component level
   * when binding to a long-lived view.
   */
  public cashFlowEntriesDataSource(scope?: TreasuryCashFlowEntriesScope): DataSource<CashFlowEntry> {
    return this.dxOdataStoreService.fromOdata<CashFlowEntry>({
      url: this.baseUrl,
      metadata: 'OData/CashFlowEntries',
      key: 'CashFlowEntryId',
      params: this.buildCashFlowScopeParams(scope),
      fieldTypes: { ...CASH_FLOW_ENTRIES_FIELD_TYPES },
      // Surfaces the last outgoing OData request on `ds.request()` so the
      // store can mirror the grid's `$filter` into the aggregates query.
      exposeRequest: true,
    });
  }

  private buildCashFlowScopeParams(
    scope?: TreasuryCashFlowEntriesScope,
  ): Record<string, string> | undefined {
    // `rawFilter` wins: it already encodes the grid's full filter expression.
    if (scope?.rawFilter) {
      return { $filter: scope.rawFilter };
    }
    const clauses: string[] = [];
    if (scope?.financialAccountId) {
      clauses.push(`FinancialAccountId eq ${scope.financialAccountId}`);
    }
    if (scope?.cashSessionId) {
      clauses.push(`CashSessionId eq ${scope.cashSessionId}`);
    }
    if (scope?.unreconciledOnly) {
      clauses.push('ReconciledTransactionId eq null');
    }
    if (scope?.chartOfAccountsNodeId) {
      clauses.push(`ChartOfAccountsNodeId eq ${scope.chartOfAccountsNodeId}`);
    }
    if (scope?.competenceDateRange) {
      const startDate = this.toBackendDateOnly(
        scope.competenceDateRange.startDate,
      );
      const endDate = this.toBackendDateOnly(scope.competenceDateRange.endDate);
      clauses.push(`CompetenceDate ge ${startDate}`);
      clauses.push(`CompetenceDate le ${endDate}`);
    }
    if (scope?.dreEligibleOnly) {
      clauses.push(`Status eq 'Confirmed'`);
      clauses.push(`EntryType ne 'Transfer'`);
      clauses.push(`EntryType ne 'Supply'`);
      clauses.push(`EntryType ne 'Withdrawal'`);
    }
    const search = scope?.searchText?.trim().toLowerCase();
    if (search) {
      const literal = `'${search.replace(/'/g, "''")}'`;
      clauses.push(
        `(contains(tolower(Description),${literal}) or contains(tolower(Category),${literal}))`,
      );
    }
    return clauses.length > 0 ? { $filter: clauses.join(' and ') } : undefined;
  }

  /**
   * Extracts the DateOnly component (`yyyy-MM-dd`) from an ISO datetime.
   * Same idea as the equivalent helper in `dre-report.service.ts` — the
   * app convention is to carry full ISO internally and only reduce it at
   * the HTTP boundary. `setZone: true` preserves the original offset so the
   * day does not slip. Pass-through on invalid input or already-DateOnly.
   */
  private toBackendDateOnly(iso: string): string {
    const parsed = getDateTime(iso);
    if (!parsed?.isValid) return iso;
    return parsed.toISODate() ?? iso;
  }

  /**
   * `CompetenceDate` predicate for the home "Resumo do dia" `$apply` queries.
   *
   * The column is `Edm.Date` — a pure day, with no time and no timezone — so
   * both bounds are reduced to `yyyy-MM-dd` here and no `Z` suffix is emitted.
   * Callers keep handing in the full ISO datetimes produced by `todayRange()` /
   * `dailySeriesRange()` (whose UTC label already spells the operator's local
   * calendar day), which reduce cleanly to that day.
   *
   * A window that collapses to a single day emits `eq`, matching the treasury
   * integration contract for the single-day case; wider windows emit `ge`/`le`.
   */
  private buildCompetenceDateFilter(range: TreasuryDateRange): string {
    const startDate = this.toBackendDateOnly(range.StartDate);
    const endDate = this.toBackendDateOnly(range.EndDate);
    if (startDate === endDate) {
      return `CompetenceDate eq ${startDate}`;
    }
    return `CompetenceDate ge ${startDate} and CompetenceDate le ${endDate}`;
  }

  /**
   * Total OData rows for a session, via a one-shot DataSource load
   * (`$count=true&$top=0`).
   */
  public countCashFlowEntriesForSession(cashSessionId: string): Observable<number> {
    const ds = this.cashFlowEntriesDataSource({ cashSessionId });
    ds.requireTotalCount(true);
    ds.paginate(true);
    ds.pageSize(0);
    return from(ds.load().then(() => ds.totalCount()));
  }

  /**
   * Loads every cash-flow entry of a single session (turn detail).
   * Bounded one-shot DataSource load — sessions are scoped to an operator
   * working day and fit in a single page in practice. Two guard-rails on top
   * of the bare load:
   *
   * - `$select` trims the projection to the columns the turn UI actually
   *   reads (see {@link SESSION_ENTRIES_SELECT}).
   * - `requireTotalCount(true)` lets us detect the silent truncation case:
   *   when the server reports more rows than the cap, the totals/list shown
   *   to the operator are wrong, so we log a warning instead of pretending
   *   everything is fine.
   */
  public getSessionCashFlowEntries(
    cashSessionId: string,
  ): Observable<CashFlowEntry[]> {
    const scopeParams =
      this.buildCashFlowScopeParams({ cashSessionId }) ?? {};
    const ds = this.dxOdataStoreService.fromOdata<CashFlowEntry>({
      url: this.baseUrl,
      metadata: 'OData/CashFlowEntries',
      key: 'CashFlowEntryId',
      params: { ...scopeParams, $select: SESSION_ENTRIES_SELECT },
      fieldTypes: { ...CASH_FLOW_ENTRIES_FIELD_TYPES },
    });
    ds.sort({ selector: 'AddedOnUtc', desc: false });
    ds.requireTotalCount(true);
    ds.paginate(true);
    ds.pageSize(SESSION_ENTRIES_PAGE_CAP);
    return from(
      ds.load().then((rows) => {
        const total = ds.totalCount();
        if (
          typeof total === 'number' &&
          total > SESSION_ENTRIES_PAGE_CAP
        ) {
          console.warn(
            `[treasury] CashSession ${cashSessionId} has ${total} entries; page cap is ${SESSION_ENTRIES_PAGE_CAP}. Rendered view is truncated.`,
          );
        }
        return (rows as ODataCashFlowEntryRow[]).map(
          mapODataRowToCashFlowEntry,
        );
      }),
    );
  }

  public getCashFlowEntry(id: string): Observable<CashFlowEntry | null> {
    return this.http
      .get<CashFlowEntry | CashFlowEntry[] | null>(`${this.baseUrl}/CashFlowEntry`, {
        params: new HttpParams({ fromObject: { id } }),
        withCredentials: true,
      })
      .pipe(map((body) => this.singleOrNull(body)));
  }

  public confirmCashFlowEntry(cashFlowEntryId: string): Observable<unknown> {
    return this.http.post<unknown>(
      `${this.baseUrl}/CashFlowEntry/ConfirmEntry`,
      null,
      {
        params: new HttpParams({
          fromObject: { CashFlowEntryId: cashFlowEntryId },
        }),
        withCredentials: true,
      },
    );
  }

  public cancelCashFlowEntry(cashFlowEntryId: string): Observable<unknown> {
    return this.http.post<unknown>(
      `${this.baseUrl}/CashFlowEntry/CancelEntry`,
      null,
      {
        params: new HttpParams({
          fromObject: { CashFlowEntryId: cashFlowEntryId },
        }),
        withCredentials: true,
      },
    );
  }

  public createManualCashFlowEntry(
    request: TreasuryManualCashFlowEntryRequest,
  ): Observable<unknown> {
    return this.http.post<unknown>(
      `${this.baseUrl}/CashFlowEntry/CreateManualEntry`,
      null,
      {
        params: this.toQueryParams(request),
        withCredentials: true,
      },
    );
  }

  /**
   * Account-scoped (or tenant-scoped) cash-flow report. Backed by OData —
   * `CashFlowReportLine` is structurally a subset of `CashFlowEntry`, so we
   * map the OData rows directly. `RunningBalance` is no longer provided
   * (no production consumer reads it; e2e fixtures are unaffected).
   */
  public getCashFlowReport(
    request: TreasuryHomeRequest,
  ): Observable<CashFlowReportLine[]> {
    const filter = this.buildCashFlowReportFilter(request);
    const ds = this.dxOdataStoreService.fromOdata<CashFlowEntry>({
      url: this.baseUrl,
      metadata: 'OData/CashFlowEntries',
      key: 'CashFlowEntryId',
      params: filter ? { $filter: filter } : undefined,
      fieldTypes: { ...CASH_FLOW_ENTRIES_FIELD_TYPES },
    });
    ds.sort({ selector: 'AddedOnUtc', desc: true });
    ds.paginate(true);
    ds.pageSize(1000);
    return from(
      ds
        .load()
        .then(
          (rows) =>
            (rows as ODataCashFlowEntryRow[]).map(
              mapODataRowToCashFlowEntry,
            ) as CashFlowReportLine[],
        ),
    );
  }

  /**
   * Loads the totals for the account-detail summary cards (`totalIn` /
   * `totalOut` / `totalReconciled`) via up to two parallel OData `$apply`
   * calls:
   *
   * 1. `$apply=filter(<scope>)/groupby((Direction),aggregate(Amount with sum as Total))`
   *    — yields per-direction sums, folded into `totalIn` / `totalOut`. The
   *    groupby intentionally does NOT include `ReconciliationStatus`; the
   *    direction totals are independent of reconciliation status.
   * 2. `$apply=filter(<scope> and ReconciliationStatus eq 'Reconciled')/aggregate(Amount with sum as Total)`
   *    — yields a single sum across both directions for reconciled rows,
   *    feeding `totalReconciled`. **Only fired when** `scope.includeReconciled !== false`;
   *    cash accounts don't reconcile so the page-feature passes `false`.
   *
   * Filter precedence (in both calls): `scope.rawFilter` (the grid's outgoing
   * `$filter`) → `scope.financialAccountId` → no filter at all. Either way the
   * cards mirror exactly what the grid is showing.
   */
  public getCashFlowAggregates(
    scope: CashFlowAggregatesScope,
  ): Observable<CashFlowAggregates> {
    const baseFilter = scope.rawFilter
      ? scope.rawFilter
      : scope.financialAccountId
        ? `FinancialAccountId eq ${scope.financialAccountId}`
        : null;

    const directionGroupby =
      'groupby((Direction),aggregate(Amount with sum as Total))';
    const directionApply = baseFilter
      ? `filter(${baseFilter})/${directionGroupby}`
      : directionGroupby;

    const url = `${this.baseUrl}/OData/CashFlowEntries`;
    const direction$ = this.http
      .get<
        ODataCashFlowDirectionAggregateResponse | ODataCashFlowDirectionAggregateRow[]
      >(url, {
        params: new HttpParams({ fromObject: { $apply: directionApply } }),
        withCredentials: true,
      })
      .pipe(map(mapCashFlowDirectionAggregateRows));

    if (scope.includeReconciled === false) {
      return direction$.pipe(
        map(({ totalIn, totalOut }) => ({
          totalIn,
          totalOut,
          totalReconciled: 0,
        })),
      );
    }

    // `ReconciliationStatus` on the backend is the canonical reconciliation
    // enum (`Pending=1, Matched=2, ManualMatch=3, Exception=4, …`) — *not*
    // the legacy treasury enum which had a non-existent `Reconciled` member.
    // An entry counts as "reconciliada" when its status is either `Matched`
    // (auto-matched by rules/ML/suggestion) or `ManualMatch` (operator-paired);
    // both contribute to the `totalReconciled` card.
    const reconciledLiteral =
      `(ReconciliationStatus eq ${ReconciliationStatus.Matched}` +
      ` or ReconciliationStatus eq ${ReconciliationStatus.ManualMatch})`;
    const reconciledAggregate = 'aggregate(Amount with sum as Total)';
    const reconciledFilter = baseFilter
      ? `(${baseFilter}) and ${reconciledLiteral}`
      : reconciledLiteral;
    const reconciledApply = `filter(${reconciledFilter})/${reconciledAggregate}`;
    const reconciled$ = this.http
      .get<
        ODataCashFlowSumAggregateResponse | ODataCashFlowSumAggregateRow[]
      >(url, {
        params: new HttpParams({ fromObject: { $apply: reconciledApply } }),
        withCredentials: true,
      })
      .pipe(map(mapCashFlowSumAggregateRow));

    return forkJoin({
      direction: direction$,
      totalReconciled: reconciled$,
    }).pipe(
      map(({ direction, totalReconciled }) => ({
        totalIn: direction.totalIn,
        totalOut: direction.totalOut,
        totalReconciled,
      })),
    );
  }

  /**
   * Running net balance per **local day** (UTC-3), restricted to the
   * `[startUtc, endUtcExclusive)` `AddedOnUtc` window — in practice the span of
   * days currently visible in the grid's loaded page. Rather than aggregating
   * the whole history, this fires three cheap parallel OData calls:
   *
   * 1. **Per-day net within the window** (`scope`, the account-only filter) —
   *    `filter(<scope> and AddedOnUtc in window)/compute(year/month/day of
   *    AddedOnUtc-3h as Y/M/D)/groupby((Y,M,D,Direction),aggregate(Amount with
   *    sum as Total,$count as Count))`. Drives both the running balance and the
   *    banner's movement count, so it deliberately ignores the grid's header
   *    filters — both reflect the real account state and stay in lockstep.
   * 2. **Carried-forward balance** (`scope`) — `filter(<scope> and AddedOnUtc lt
   *    startUtc)/groupby((Direction),aggregate(Amount with sum as Total))`,
   *    folded into a single net (`in − out`) that seeds the running balance so
   *    the first visible day already reflects everything before it.
   * 3. **Per-day separator key** (`gridFilter`, the grid's full `$filter`) —
   *    `filter(<gridFilter> and AddedOnUtc in window)/compute(.../groupby((Y,M,D),
   *    aggregate(AddedOnUtc with max as MaxAddedOnUtc))`. Because it honours the
   *    grid's filters, `MaxAddedOnUtc` is the day's last *visible* row, so the
   *    day banner always lands on a row the grid is actually rendering (the
   *    unfiltered net query could otherwise key a hidden row).
   *
   * Deriving the day from `AddedOnUtc` (not the stored `CompetenceDate`) keeps
   * the keys aligned with the local calendar day the entry was recorded on.
   *
   * @param scope     account-only scope for the (unfiltered) balance queries.
   * @param gridFilter the grid's verbatim `$filter` (already includes the account
   *                   pre-scope) for the per-day separator-key query; when null
   *                   the key query falls back to `scope`.
   */
  public getCashFlowEntriesByDay(
    scope: TreasuryCashFlowEntriesScope | undefined,
    gridFilter: string | null,
    loadedSpan: { minLocal: string; maxLocal: string },
    initialBalance = 0,
  ): Observable<CashFlowEntriesByDayMap> {
    const baseFilter = this.buildCashFlowScopeParams(scope)?.['$filter'] ?? null;
    const and = (base: string | null, clause: string): string =>
      base ? `(${base}) and ${clause}` : clause;
    const url = `${this.baseUrl}/OData/CashFlowEntries`;

    // Shift UTC `AddedOnUtc` to the owner's configured timezone before deriving
    // the calendar day, so buckets follow the same local day the UI renders.
    const shift = this.utcToLocalIsoDuration();
    const compute =
      `compute(year(AddedOnUtc add duration'${shift}') as Y,` +
      `month(AddedOnUtc add duration'${shift}') as M,` +
      `day(AddedOnUtc add duration'${shift}') as D)`;

    // Widen the loaded rows' instant span to whole local days: [start of the
    // min row's local day, start of the day after the max row's local day).
    // Aggregating full days keeps the running balance and per-day `MaxAddedOnUtc`
    // correct, and a half-open `[ge, lt)` bound on day edges never clips a record
    // by its sub-second precision — which `le` on a millisecond-truncated instant
    // does, since DevExtreme returns `AddedOnUtc` as a ms-precision Date.
    const windowStartLocal = this.localDayStart(loadedSpan.minLocal, 0);
    const windowEndLocal = this.localDayStart(loadedSpan.maxLocal, 1);
    const window = `AddedOnUtc ge ${windowStartLocal} and AddedOnUtc lt ${windowEndLocal}`;

    const perDayApply =
      `filter(${and(baseFilter, window)})/${compute}/` +
      'groupby((Y,M,D,Direction),aggregate(Amount with sum as Total,$count as Count))';
    const perDay$ = this.http.get<
      | ODataCashFlowDayAggregateResponse
      | ODataCashFlowDayAggregateRow[]
    >(url, {
      params: new HttpParams({ fromObject: { $apply: perDayApply } }),
      withCredentials: true,
    });

    const priorApply =
      `filter(${and(baseFilter, `AddedOnUtc lt ${windowStartLocal}`)})/` +
      'groupby((Direction),aggregate(Amount with sum as Total))';
    const prior$ = this.http
      .get<
        | ODataCashFlowDirectionAggregateResponse
        | ODataCashFlowDirectionAggregateRow[]
      >(url, {
        params: new HttpParams({ fromObject: { $apply: priorApply } }),
        withCredentials: true,
      })
      .pipe(map(mapCashFlowDirectionAggregateRows));

    // Honours the grid's filters so the day's `MaxAddedOnUtc` is a visible row.
    const dayKeyApply =
      `filter(${and(gridFilter ?? baseFilter, window)})/${compute}/` +
      'groupby((Y,M,D),aggregate(AddedOnUtc with max as MaxAddedOnUtc))';
    const dayKey$ = this.http.get<
      ODataCashFlowDayKeyResponse | ODataCashFlowDayKeyRow[]
    >(url, {
      params: new HttpParams({ fromObject: { $apply: dayKeyApply } }),
      withCredentials: true,
    });

    return forkJoin({ perDay: perDay$, prior: prior$, dayKey: dayKey$ }).pipe(
      map(({ perDay, prior, dayKey }) =>
        mapCashFlowEntriesByDayRows(
          perDay,
          dayKey,
          // Always seed the running balance with the account's configured
          // `InitialBalance` (from `GET /FinancialAccount?id`) on top of the
          // net of all entries before the loaded window, so the day banner
          // reflects the real account balance, not just the sum of operations.
          initialBalance + prior.totalIn - prior.totalOut,
        ),
      ),
    );
  }

  /**
   * ISO-8601 duration that shifts a UTC instant to wall-clock time in the
   * account's timezone, e.g. `-PT3H` for `America/Sao_Paulo`, `PT0S` for UTC,
   * `-PT5H30M` for a half-hour offset. Fed verbatim into the OData
   * `compute(... AddedOnUtc add duration'<d>' ...)` so the per-day grouping uses
   * the owner's local calendar day.
   *
   * O fuso vem do `DateTimeService.getUserZone()`, que lê `ownerSettings()` — um
   * `computed`, logo síncrono e correto desde o primeiro pedido. NÃO usar
   * `DateTime.now().offset`: esse é o `Settings.defaultZone`, que o
   * `LUXON_ZONE_INITIALIZER` só preenche a partir de um `effect()`, ou seja
   * depois do primeiro flush de change detection — um pedido que saia antes
   * disso montaria o `compute` com o offset da MÁQUINA e o servidor agruparia
   * as linhas no dia de calendário errado, em silêncio. É a mesma regra que o
   * `adjustODataFilterDatesToUtc` já impõe, e pela mesma razão.
   *
   * O `localDayStart` não precisa da mesma cautela: só manipula dígitos de
   * hora de parede (`startOf('day')`, `plus({ days })`), e o resultado é o mesmo
   * em qualquer fuso — não há como os dois discordarem.
   *
   * ## Limitação conhecida
   *
   * Usa UM offset — o de agora — para TODAS as linhas do intervalo. Se a página
   * carregada atravessar uma transição de horário de verão, as linhas do outro
   * lado são agrupadas com o offset errado e um movimento perto da meia-noite
   * cai no dia errado do gráfico. Não tem correção deste lado: exigiria o
   * servidor a resolver o fuso IANA por linha em vez de somar uma duração fixa.
   * Inofensivo enquanto as contas não observarem horário de verão, que é a
   * premissa em que o resto desta app também assenta.
   */
  private utcToLocalIsoDuration(): string {
    const offsetMinutes = DateTime.now().setZone(
      this.dateTimeService.getUserZone(),
    ).offset;
    if (!Number.isFinite(offsetMinutes) || offsetMinutes === 0) return 'PT0S';
    const sign = offsetMinutes < 0 ? '-' : '';
    const abs = Math.abs(offsetMinutes);
    const hours = Math.floor(abs / 60);
    const minutes = abs % 60;
    return `${sign}PT${hours ? `${hours}H` : ''}${minutes ? `${minutes}M` : ''}`;
  }

  /**
   * Início do dia de calendário que contém `localIso`, opcionalmente deslocado
   * `dayOffset` dias inteiros. Serve para transformar os extremos das linhas
   * carregadas em limites de dia inteiro, meio-abertos, para o `AddedOnUtc`.
   *
   * ENTRA e SAI hora de parede da conta — daí `localIso` e não `instantUtc`. É a
   * forma em que as cláusulas de `AddedOnUtc` são escritas: o
   * `adjustODataFilterDatesToUtc` converte as comparações cujo atributo contém
   * "Utc", lendo o literal como local. Converter para instante UTC aqui também
   * deslocaria a janela duas vezes.
   *
   * Ao contrário do {@link utcToLocalIsoDuration}, não precisa de saber o fuso:
   * só mexe nos dígitos (`startOf('day')`, `plus({ days })`, `toISO()` sem
   * offset), e o resultado é o mesmo em qualquer `Settings.defaultZone`. Por
   * isso não há como os dois discordarem na janela de arranque.
   *
   * Devolve a string de entrada quando ela não faz parse.
   */
  private localDayStart(
    localIso: string,
    dayOffset: number,
  ): string {
    const local = getDateTime(localIso);
    if (!local?.isValid) return localIso;
    return (
      local.startOf('day').plus({ days: dayOffset }).toISO() ??
      localIso
    );
  }

  private buildCashFlowReportFilter(
    request: TreasuryHomeRequest,
  ): string | null {
    const clauses: string[] = [];
    if (request.FinancialAccountId) {
      clauses.push(`FinancialAccountId eq ${request.FinancialAccountId}`);
    }
    if (request.StartDate) {
      clauses.push(`AddedOnUtc ge ${request.StartDate}`);
    }
    if (request.EndDate) {
      clauses.push(`AddedOnUtc le ${request.EndDate}`);
    }
    return clauses.length > 0 ? clauses.join(' and ') : null;
  }

  /**
   * Tenant-wide daily totals over a date window, aggregated server-side via
   * OData `$apply=filter(...)/groupby((CompetenceDate,Direction,EntryType,FinancialAccountId),aggregate(Amount with sum as Total,$count as Count))`.
   *
   * Used by the home "Resumo do dia" sparkline (7-day trend) and the per-point
   * account breakdown surfaced through the chart tooltip. Each resulting point
   * feeds simultaneously into multiple cards: a row with `Direction=In` AND
   * `EntryType=Adjustment` contributes to **both** the Entries and Adjustments
   * sparklines — that overlap is intentional and matches the client-side
   * classification used by `dailyTotals`. Rows with `EntryType=Transfer` are
   * excluded from Entries/Exits sparklines (both legs); outbound transfer
   * volume appears only in the Transfers series.
   *
   * `FinancialAccountId` is part of the groupby tuple so the same payload can
   * power the per-account composition shown when hovering each data point.
   * Consumers that need the sparkline line itself (sum across accounts) must
   * collapse the account dimension client-side; the per-day bucketing is also
   * client-side (see `treasury.store.ts` calling `isoDateOnly`), so a server
   * that returns `CompetenceDate` with a time component still lands on the
   * right day.
   */
  public getTenantDailySeries(
    request: TreasuryDateRange,
  ): Observable<TenantDailySeriesPoint[]> {
    const filter = this.buildCompetenceDateFilter(request);
    const apply =
      `filter(${filter})/groupby((CompetenceDate,Direction,EntryType,FinancialAccountId),` +
      `aggregate(Amount with sum as Total,$count as Count))`;
    const ds = this.dxOdataStoreService.fromOdata<ODataDailySeriesRow>({
      url: this.baseUrl,
      metadata: 'OData/CashFlowEntries',
      key: 'CashFlowEntryId',
      params: { $apply: apply },
      fieldTypes: { ...CASH_FLOW_ENTRIES_FIELD_TYPES },
    });
    return from(
      ds
        .load()
        .then((rows) =>
          mapODataDailySeriesResponse((rows ?? []) as ODataDailySeriesRow[]),
        ),
    );
  }

  /**
   * Tenant-wide totals for the home "today" cards, aggregated server-side via
   * OData `$apply=filter(CompetenceDate window)/groupby((Direction,EntryType,PaymentMethod,FinancialAccountId),aggregate(Amount with sum as Total,$count as Count))`.
   *
   * Replaces the legacy `getCashFlowReport({StartDate, EndDate})` path that
   * pulled every row of the day with `$top=1000` only to reduce client-side.
   * The new payload is bounded by `2 dirs × 4 entry types × ~6 methods × N
   * accounts` — at most a few dozen rows for a typical tenant — and lets each
   * home computed (`dailyTotals`, `dailyTotalsByAccount`, `dailyByPaymentMethod`)
   * collapse exactly the dimensions it needs.
   *
   * The window filters `CompetenceDate` — the day the entry *belongs to*
   * financially — and not `AddedOnUtc`, the instant the row was written. An
   * entry recorded today for a past day therefore lands on the day it refers
   * to, instead of inflating today's cards. The flip side is that reloading an
   * already-closed day can legitimately show a different total once someone
   * records a backdated entry against it.
   */
  public getTenantDailyAggregates(
    request: TreasuryDateRange,
  ): Observable<TenantDailyAggregatePoint[]> {
    const filter = this.buildCompetenceDateFilter(request);
    const apply =
      `filter(${filter})/` +
      `groupby((Direction,EntryType,PaymentMethod,FinancialAccountId),` +
      `aggregate(Amount with sum as Total,$count as Count))`;
    const ds = this.dxOdataStoreService.fromOdata<ODataTenantDailyAggregateRow>({
      url: this.baseUrl,
      metadata: 'OData/CashFlowEntries',
      key: 'CashFlowEntryId',
      params: { $apply: apply },
      fieldTypes: { ...CASH_FLOW_ENTRIES_FIELD_TYPES },
    });
    return from(
      ds.load().then((rows) =>
        mapODataTenantDailyAggregateResponse(
          (rows ?? []) as ODataTenantDailyAggregateRow[],
        ),
      ),
    );
  }

  public getReconciliationDashboard(
    request: TreasuryHomeRequest,
  ): Observable<TreasuryDashboardPayload> {
    return this.http
      .get<TreasuryDashboardPayload>(
        `${this.baseUrl}/ReconciliationReport/GetDashboard`,
        {
          params: this.toQueryParams({
            BankConnectionId: request.BankConnectionId,
            FromDate: request.StartDate,
            ToDate: request.EndDate,
          }),
          withCredentials: true,
        },
      )
      .pipe(
        map((body) => {
          if (body !== null && typeof body === 'object' && !Array.isArray(body)) {
            return body as TreasuryDashboardPayload;
          }
          return {} as TreasuryDashboardPayload;
        }),
      );
  }

  public getCashOperation(id: string): Observable<CashOperation | null> {
    return this.http
      .get<CashOperation | CashOperation[] | null>(
        `${this.baseUrl}/CashOperation`,
        {
          params: new HttpParams({ fromObject: { id } }),
          withCredentials: true,
        },
      )
      .pipe(map((body) => this.singleOrNull(body)));
  }

  public createCashOperation(
    request: TreasuryCreateCashOperationRequest,
  ): Observable<CashOperation | null> {
    return this.http
      .post<CashOperation | CashOperation[] | null>(
        `${this.baseUrl}/CashOperation/CreateOperation`,
        null,
        {
          params: this.toQueryParams(request),
          withCredentials: true,
        },
      )
      .pipe(map((body) => this.singleOrNull(body)));
  }

  public getCashTransfers(): Observable<CashTransfer[]> {
    return this.http
      .request<CashTransfer[]>('OPTIONS', `${this.baseUrl}/CashTransfer`, {
        withCredentials: true,
      })
      .pipe(map((body) => (Array.isArray(body) ? body : [])));
  }

  public getCashTransfer(id: string): Observable<CashTransfer | null> {
    return this.http
      .get<CashTransfer | CashTransfer[] | null>(`${this.baseUrl}/CashTransfer`, {
        params: new HttpParams({ fromObject: { id } }),
        withCredentials: true,
      })
      .pipe(map((body) => this.singleOrNull(body)));
  }

  public createCashTransfer(
    request: TreasuryCreateCashTransferRequest,
  ): Observable<CashTransfer | null> {
    return this.http
      .post<CashTransfer | CashTransfer[] | null>(
        `${this.baseUrl}/CashTransfer/CreateTransfer`,
        null,
        {
          params: this.toQueryParams({
            FromAccountId: request.FromAccountId,
            ToAccountId: request.ToAccountId,
            Amount: request.Amount ?? 0,
            TransferDate: request.TransferDate,
            Description: request.Description ?? '',
          }),
          withCredentials: true,
        },
      )
      .pipe(map((body) => this.singleOrNull(body)));
  }

  private singleOrNull<T>(body: T | T[] | null): T | null {
    if (body == null) return null;
    if (Array.isArray(body)) return body[0] ?? null;
    return body;
  }

  private toQueryParams(params?: object): HttpParams {
    const entries = Object.entries(
      (params ?? {}) as Record<string, unknown>,
    ).reduce<Record<string, string>>((acc, [key, value]) => {
      if (value === null || value === undefined || value === '') {
        return acc;
      }
      acc[key] = typeof value === 'string' ? value : String(value);
      return acc;
    }, {});
    return new HttpParams({ fromObject: entries });
  }
}
